// Minimal i18n. 13 languages, RTL for fa/ar/ur.
// t(key, params?) does {token} interpolation. Missing keys fall back to English.
// زبان انتخابی روی دستگاه می‌مونه (از پشتِ پورتِ storage)؛ اولین بار از زبان گوشی حدس زده میشه.
//
// از فاز ۵ به بعد: هر زبان جز انگلیسی جدا و **تنبل** بارگذاری می‌شه
// (i18nLocales/*.ts، با importِ داینامیک در loadDict پایینِ همین فایل).
//
// چرا: این فایل قبلاً هر سیزده دیکشنری را همیشه با هم می‌بست — روی موبایل
// مشکلی نبود (یک‌بار نصب می‌شود)، ولی روی وب یعنی هر بازدیدکننده حدود ۳۰۰
// کیلوبایتِ فشرده‌ی ترجمه‌ی زبان‌هایی که هرگز نمی‌بیند دانلود می‌کرد. با
// Vite/Rollup هر importِ داینامیک یک فایلِ جداگانه می‌شود؛ فقط زبانِ لازم
// از شبکه می‌آید. روی Metro (موبایل) این importها همچنان در همان یک باندل
// جا می‌گیرند — Metro در حالتِ استاندارد lazy-chunk ندارد — پس رفتارِ موبایل
// دقیقاً همان قبل می‌ماند، فقط ترتیبِ کد فرق کرده.
//
// انگلیسی عمداً استثناست: همیشه به‌صورتِ ایستا import می‌شود، چون
// (۱) fallbackِ نهاییِ هر کلیدِ گم‌شده است و باید همیشه در دسترس باشد،
// (۲) پیشفرضِ اولین رندر است تا زبانِ واقعی معلوم شود.
import { useEffect, useReducer } from 'react';
import { storage } from '../platform.ts';
import { recordPref } from './prefSink.ts';
import { reportError } from './report.ts';
import enDict from './i18nLocales/en.ts';

export type Locale = 'en' | 'fr' | 'fa' | 'ar' | 'es' | 'zh' | 'hi' | 'de' | 'pa' | 'ur' | 'tl' | 'yue' | 'sv';
type Dict = Record<string, string>;

/**
 * دیکشنری‌های بارگذاری‌شده. انگلیسی از همون اول پره؛ بقیه با اولین لازم‌شدن
 * پر می‌شن و همون‌جا می‌مونن — یعنی هر زبان حداکثر یک‌بار در طولِ عمرِ صفحه
 * از شبکه می‌آد.
 */
const cache: Partial<Record<Locale, Dict>> = { en: enDict };

/**
 * هر شاخه یک importِ داینامیکِ با مسیرِ **رشته‌ای ثابت** است — عمدی: هم
 * Vite/Rollup و هم Metro فقط مسیرِ رشته‌ای ثابت را برای تفکیکِ باندل تشخیص
 * می‌دهند؛ یک importِ متغیر (`import(\`./i18nLocales/${l}.ts\`)`) را نمی‌شناسند
 * یا همه را با هم می‌بندند — دقیقاً همان مشکلی که می‌خواهیم حلش کنیم.
 */
async function loadDict(l: Locale): Promise<Dict> {
  switch (l) {
    case 'en': return enDict;
    case 'fr': return (await import('./i18nLocales/fr.ts')).default;
    case 'fa': return (await import('./i18nLocales/fa.ts')).default;
    case 'ar': return (await import('./i18nLocales/ar.ts')).default;
    case 'es': return (await import('./i18nLocales/es.ts')).default;
    case 'zh': return (await import('./i18nLocales/zh.ts')).default;
    case 'hi': return (await import('./i18nLocales/hi.ts')).default;
    case 'de': return (await import('./i18nLocales/de.ts')).default;
    case 'pa': return (await import('./i18nLocales/pa.ts')).default;
    case 'ur': return (await import('./i18nLocales/ur.ts')).default;
    case 'tl': return (await import('./i18nLocales/tl.ts')).default;
    case 'yue': return (await import('./i18nLocales/yue.ts')).default;
    case 'sv': return (await import('./i18nLocales/sv.ts')).default;
    default: return enDict;
  }
}

/** بارگذاری با کش؛ صدها لمسِ همزمانِ `ensureLoaded` همون یه Promise رو به اشتراک می‌ذارن. */
let inFlight: Partial<Record<Locale, Promise<Dict>>> = {};
async function ensureLoaded(l: Locale): Promise<Dict> {
  const hit = cache[l];
  if (hit) return hit;
  const pending = inFlight[l] ?? loadDict(l).then((d) => {
    cache[l] = d;
    delete inFlight[l];
    return d;
  }).catch((e) => {
    delete inFlight[l];
    reportError('i18n.loadDict', e, { locale: l });
    return enDict; // شکستِ شبکه نباید کلِ زبان رو غیرقابل‌استفاده کنه
  });
  inFlight[l] = pending;
  return pending;
}

const isKnownLocale = (s: string): s is Locale => LOCALES.some((x) => x.code === s);

// Ordered list for the language picker, each with its native name.
// عمداً همیشه ایستاست: کاربر باید بتونه فهرستِ سیزده زبان رو ببینه بدونِ
// اینکه سیزده دیکشنریِ کاملشون بارگذاری شده باشه.
export const LOCALES: { code: Locale; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'fa', name: 'فارسی' },
  { code: 'ar', name: 'العربية' },
  { code: 'es', name: 'Español' },
  { code: 'zh', name: '中文' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ' },
  { code: 'ur', name: 'اردو' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'yue', name: '粵語' },
  { code: 'sv', name: 'Svenska' },
];

const RTL_LOCALES: Locale[] = ['fa', 'ar', 'ur'];

let current: Locale = 'en';
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

const STORE_KEY = 'goout.locale.v1';

/**
 * زبان انتخابی رو از حافظه‌ی دستگاه برمی‌گردونه. باید قبل از اولین رندر صدا زده بشه،
 * وگرنه اپ با انگلیسی بالا میاد و بعد می‌پره روی زبان درست.
 * اگه کاربر هنوز زبانی انتخاب نکرده، از زبان خود گوشی حدس می‌زنه.
 *
 * `await ensureLoaded` عمداً قبلِ `emit` است: صفحه تا این‌جا هنوز رندر نشده
 * (هر دو اپ منتظرِ همین تابع می‌مونن)، پس بهترین زمانه که بارِ شبکه رو تحمل
 * کنیم — نه وسطِ استفاده، با یه فلاشِ متنِ انگلیسی.
 */
export const initLocale = async (): Promise<void> => {
  try {
    const saved = await storage().getItem(STORE_KEY);
    if (saved && isKnownLocale(saved)) {
      await ensureLoaded(saved);
      current = saved;
      emit();
      return;
    }
  } catch { /* حافظه در دسترس نیست - با پیش‌فرض ادامه می‌دیم */ }

  const device = deviceLocale();
  if (device && device !== current) {
    await ensureLoaded(device);
    current = device;
    emit();
  }
};

/**
 * برچسبِ گوشی که با بریدنِ کشور به کدِ ما نمی‌رسه.
 * گوشیِ هنگ‌کنگی `zh-HK` می‌ده و بریدنش «ماندارین» درمیاد، در حالی که کاربرش
 * کانتونی حرف می‌زنه. تاگالوگ هم توی سیستم‌عامل `fil` است نه `tl`.
 */
const TAG_ALIASES: Record<string, Locale> = {
  'zh-hk': 'yue', 'zh-mo': 'yue', 'yue': 'yue', fil: 'tl',
};

/** کد زبان گوشی (مثلاً `fa-IR` → `fa`) اگه پشتیبانی می‌کنیم. */
function deviceLocale(): Locale | null {
  try {
    const tag = (new Intl.DateTimeFormat().resolvedOptions().locale ?? '').toLowerCase();
    const region = tag.replace('_', '-').split('-').slice(0, 2).join('-');
    if (TAG_ALIASES[region]) return TAG_ALIASES[region];

    const base = tag.split(/[-_]/)[0];
    if (TAG_ALIASES[base]) return TAG_ALIASES[base];
    return isKnownLocale(base) ? base : null;
  } catch {
    return null;
  }
}

export const getLocale = (): Locale => current;

/**
 * زبانی که کاربر **صریحاً** انتخاب کرده، یا `null` اگر هیچ‌وقت نکرده.
 *
 * فرقش با `getLocale()` مهم است: آن یکی همیشه چیزی می‌دهد، حتی وقتی مقدارش
 * حدسی است که از زبانِ خودِ گوشی زده شده. این یکی فقط تصمیم را می‌گوید.
 *
 * تنها مصرفش `prefLogRepo.syncLocale` است: حدس نباید به‌عنوان انتخاب روی
 * حساب نوشته شود، وگرنه گوشیِ انگلیسیِ کسی که هیچ‌وقت زبان را دست نزده،
 * حساب را انگلیسی اعلام می‌کرد و انتخابِ واقعیِ او روی گوشیِ دوم را
 * می‌پوشاند. هم‌خانواده‌ی `hasPickedTheme()` در theme.ts.
 */
export const pickedLocale = async (): Promise<Locale | null> => {
  try {
    const saved = await storage().getItem(STORE_KEY);
    return saved && isKnownLocale(saved) ? saved : null;
  } catch {
    return null;
  }
};
export const isRTL = (): boolean => RTL_LOCALES.includes(current);
export const onLocaleChange = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};

/**
 * تعویضِ زبان — امضاش عمداً سنکرون مونده (۷۰+ جا صداش می‌زنن)، خودِ کار از
 * پشت async است.
 *
 * اگه دیکشنری از قبل کش شده (زبانِ پیش‌فرض، یا زبانی که قبلاً یه‌بار انتخاب
 * شده)، همین `emit` اول متنِ کامل رو نشون می‌ده. اگه نه، همین `emit` با
 * fallbackِ انگلیسیِ `t()` یه پوسته‌ی فوری می‌ده و وقتی دیکشنری رسید، `emit`ِ
 * دوم پرش می‌کنه — دقیقاً همون رفتاری که کتابخانه‌های i18n با بارگذاریِ
 * تنبل دارن، نه یه حالتِ خطا.
 */
export const setLocale = (l: Locale): void => {
  current = l;
  storage().setItem(STORE_KEY, l).catch(() => undefined);
  // ⚠ و روی حساب هم — تنها تنظیمی که سرور واقعاً می‌خواندش. بدونِ این،
  // تریگرهای اعلان (مایگریشن ۰۱۰۹) نمی‌دانند متنِ پوش را به چه زبانی
  // بسازند، و همان باگی برمی‌گردد که این‌جا شروع شد: انگلیسی روی اپ،
  // فارسی توی اعلان.
  recordPref('locale', l);
  emit();
  if (!cache[l]) {
    ensureLoaded(l).then(() => { if (current === l) emit(); });
  }
};

/**
 * هر کامپوننتی که متن ترجمه‌شده رندر می‌کنه و **خودش ریشه‌ی رندر داره** باید این رو صدا بزنه.
 * مخصوصاً لایوت تب‌ها: React Navigation کامپوننت‌های ناوبری رو memoize می‌کنه، پس
 * رندر دوباره‌ی ریشه به اون‌ها نمی‌رسه و بدون این هوک، منوی پایین روی زبان قبلی می‌مونه.
 */
export function useLocale(): Locale {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => onLocaleChange(force), []);
  return current;
}

export const localeName = (l: Locale = current): string =>
  LOCALES.find((x) => x.code === l)?.name ?? l;
export const nextLocale = (): Locale => {
  const i = LOCALES.findIndex((x) => x.code === current);
  return LOCALES[(i + 1) % LOCALES.length].code;
};
export const days = (): string[] => DAYS[current] ?? DAYS.en;

/**
 * برعکسِ `t()` — از متنِ دیده‌شده به کلیدش.
 *
 * ═══ چرا وجود دارد ═══
 *
 * هر دو کیتِ رابط، دکمه را با **متنِ ترجمه‌شده‌اش** برچسب می‌زنند
 * (`tapped(label, onPress)`). برای لاگِ توسعه اشکالی ندارد، ولی آمارِ
 * رفتاری دو مشکل با آن دارد:
 *
 *   ۱. متنِ آزاد در `app_events` نمی‌نشیند — قولِ سرِ `lib/analytics`. چیپی
 *      که اسمِ یک دوست را نشان می‌دهد نباید به جدولِ رفتار برود.
 *   ۲. حتی اگر می‌نشست، «دعوت از دوستان» و «Invite friends» دو ردیفِ جدا
 *      می‌شدند و آمارِ سیزده زبان سیزده تکه می‌شد.
 *
 * کلید هر دو را حل می‌کند: `inviteFriends` هم بی‌نام است هم بی‌زبان. متنی
 * که کلیدی ندارد — اسمِ آدم، تگ، شهر، یا هر رشته‌ی ساخته‌شده با `{param}` —
 * جواب `null` می‌گیرد و صداکننده `other` ثبت می‌کند. یعنی حالتِ پیش‌فرض
 * «نمی‌دانم» است، نه «متن را ذخیره کن».
 *
 * ⚠ فهرست تنبل ساخته می‌شود و با هر تعویضِ زبان می‌ریزد. ساختنش یک گذر روی
 * چند صد کلید است و فقط اولین لمسِ بعد از تعویضِ زبان هزینه‌اش را می‌دهد.
 *
 * ⚠ اگر دو کلید متنِ یکسان داشته باشند (مثلِ دو «بستن»)، اولی برنده است.
 * برای شمارشِ رفتار این خطا بی‌ضرر است و چاره‌اش — کلیدِ صریح در هر
 * صدا زدنِ کیت — صد جای دیگر را شلوغ می‌کرد.
 */
let reverse: Map<string, string> | null = null;
let reverseFor: Locale | null = null;
/**
 * ⚠ خودِ **شیءِ** دیکشنری نگه داشته می‌شود، نه فقط نامِ زبان: دیکشنریِ زبانِ
 * جاری تنبل می‌آید، و بدونِ این، فهرستی که چند صدم ثانیه زودتر ساخته شده
 * (فقط انگلیسی) تا تعویضِ زبانِ بعدی می‌ماند و هیچ برچسبِ فارسی‌ای پیدا
 * نمی‌شد.
 */
let reverseDict: Dict | undefined;

export const keyOfText = (text: string): string | null => {
  if (!text) return null;
  if (!reverse || reverseFor !== current || reverseDict !== cache[current]) {
    reverse = new Map();
    // انگلیسی اول، بعد زبانِ جاری: اگر کلیدی فقط در انگلیسی باشد هم پیدا
    // می‌شود، و متنِ زبانِ جاری روی هم‌نامِ انگلیسی‌اش نمی‌نویسد.
    for (const [k, v] of Object.entries(enDict)) if (!reverse.has(v)) reverse.set(v, k);
    for (const [k, v] of Object.entries(cache[current] ?? {})) if (!reverse.has(v)) reverse.set(v, k);
    reverseFor = current;
    reverseDict = cache[current];
  }
  return reverse.get(text) ?? null;
};

/**
 * نامِ برند — همان الگوی `setShareLinkBase` و `setAuthLinkBase`.
 *
 * چرا توکن و نه نوشتنِ اسم داخلِ هر رشته: این محصول در یک ماه سه بار اسم عوض
 * کرده (GoOut → WhosUp → Pals & Plans)، و هر بار یعنی دست بردن به ۴۳۳ رشته در
 * سیزده زبان — کاری که دفعه‌ی قبل چند جا را جا انداخت، از جمله نشانِ کلامیِ
 * بالای هر صفحه که با تگ شکسته بود و از همه‌ی جستجوها فرار کرد.
 *
 * حالا اسم یک جا نشسته و ترجمه‌ها `{brand}` می‌نویسند. نتیجه‌ی جانبی‌اش این
 * است که یک دامنه می‌تواند همان اپ را با نامِ دیگری نشان دهد — که آزمونِ
 * نامِ outami.com دقیقاً همین است.
 */
const DEFAULT_BRAND = 'Outami';
let brandName = DEFAULT_BRAND;

/** با آرگومانِ خالی به پیش‌فرض برمی‌گردد. صداکننده‌اش بوتِ اپ است. */
export function setBrandName(name?: string | null): void {
  brandName = name?.trim() || DEFAULT_BRAND;
}

/** برای جاهایی که اسم را بیرون از یک رشته‌ی ترجمه لازم دارند (عنوانِ تب و…). */
export const brand = (): string => brandName;

export const t = (key: string, params?: Record<string, string | number>): string => {
  const dict = cache[current];
  let str = dict?.[key] ?? enDict[key] ?? key;
  // همیشه، نه فقط وقتی params داده شده: `{brand}` پارامترِ صداکننده نیست.
  if (str.includes('{brand}')) str = str.replace(/\{brand\}/g, brandName);
  if (params) {
    for (const k of Object.keys(params)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(params[k]));
    }
  }
  return str;
};

/**
 * جمله‌ای که وسطش یک لینک می‌نشیند («با ادامه دادن، {terms} را می‌پذیرید»):
 * متنِ پیش و پس از `{holder}` را جدا برمی‌گرداند تا خودِ صفحه لینک را
 * میانشان بگذارد. جای لینک در هر زبان فرق می‌کند (فعل در فارسی آخر است)،
 * پس نمی‌شود جمله را دو تکه‌ی ثابت ترجمه کرد.
 */
export const tAround = (key: string, holder: string): [string, string] => {
  const MARK = '\u0000';
  const [before = '', after = ''] = t(key, { [holder]: MARK }).split(MARK);
  return [before, after];
};

// Day names, Saturday-first (matches the app's dow index: 0=Sat … 6=Fri).
const DAYS: Record<Locale, string[]> = {
  en: ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  fr: ['Samedi', 'Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'],
  fa: ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'],
  ar: ['السبت', 'الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'],
  es: ['Sábado', 'Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'],
  zh: ['周六', '周日', '周一', '周二', '周三', '周四', '周五'],
  hi: ['शनिवार', 'रविवार', 'सोमवार', 'मंगलवार', 'बुधवार', 'गुरुवार', 'शुक्रवार'],
  de: ['Samstag', 'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'],
  pa: ['ਸ਼ਨਿੱਚਰਵਾਰ', 'ਐਤਵਾਰ', 'ਸੋਮਵਾਰ', 'ਮੰਗਲਵਾਰ', 'ਬੁੱਧਵਾਰ', 'ਵੀਰਵਾਰ', 'ਸ਼ੁੱਕਰਵਾਰ'],
  ur: ['ہفتہ', 'اتوار', 'پیر', 'منگل', 'بدھ', 'جمعرات', 'جمعہ'],
  tl: ['Sabado', 'Linggo', 'Lunes', 'Martes', 'Miyerkules', 'Huwebes', 'Biyernes'],
  yue: ['星期六', '星期日', '星期一', '星期二', '星期三', '星期四', '星期五'],
  sv: ['Lördag', 'Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag'],
};
